import { addUrlArgs } from '~/helpers/tools';
import {
  DEFAULT_CLIENT_FAIL_RESPONSE,
  DEFAULT_ERROR_RESPONSE,
  DEFAULT_REQUEST_DATA,
  DEFAULT_REQUEST_TIMEOUT,
  getDefaultError,
} from './constants';

import { BASE_URL } from '~/config';

export type APIConfig = Required<ApiTypes.HttpRequestConfig>;

export type APIResponse = {
  status: number;
  data: any;
};

/**
 * 该类使用了 ts 的抽象类的方式作为 HTTP Request 的基础封装， 抽离公共方法， 作为基类
 * 其子类是由： fetch 和 xhr
 */
export default abstract class APIBaseProxy {
  // 请求的 config
  protected config!: APIConfig;
  // 对请求进行数据 Response 的缓存， 该缓存策略是： 在短时间内内多次请求， 则只发送一次请求，
  // 并返回缓存的结果， key 为 url 和 参数的序列化的值
  protected cache = new Map();

  public constructor(config: ApiTypes.HttpRequestConfig) {
    this.config = config as APIConfig;
  }

  // 通用方法
  public send(): Promise<APIResponse> {
    const url = this.genUrl();
    return this.handleSendRequest(url);
  }

  // 校验 http request config 必须参数
  public validateConfig(): boolean {
    const { path, timeout, data, method } = this.config;
    if (!method) {
      return false;
    }
    if (!path) {
      return false;
    }
    if ((timeout as number) <= 0) {
      return false;
    }

    this.config.timeout = DEFAULT_REQUEST_TIMEOUT;

    if (!data) {
      this.config.data = DEFAULT_REQUEST_DATA;
    }
    this.config.headers = this.config.headers ?? ({} as any);
    return true;
  }

  // 格式化 url， 同时解析 url query 参数
  protected genUrl() {
    const {
      path,
      query = {},
      useEncode = true,
      baseUrl = BASE_URL,
    } = this.config;
    const url = `${baseUrl}${path}`;
    const q = Object.assign({}, query);
    return addUrlArgs(url, q, useEncode);
  }

  // 处理 post 请求的 body 参数
  protected getPostData() {
    const { headers, data = {} } = this.config;

    // note: 删除值为空字段
    if (headers['Content-Type'] === 'application/x-www-form-urlencoded') {
      const str = Object.keys(data)
        .map((k: string) => `${k}=${data[k]}`)
        .join('&');
      return str;
    }
    // eslint-disable-next-line no-restricted-syntax
    if (headers['Content-Type'] === 'multipart/form-data') {
      // 走 formData
      const formData = new FormData();
      // eslint-disable-next-line no-restricted-syntax
      for (const key in data) {
        formData.append(key, data[key]);
      }
      return formData;
    }
    return JSON.stringify(data);
  }

  // 处理 Response 结果
  protected handleRes(
    state: 'error' | 'fail' | 'success',
    status: number,
    data: any
  ): APIResponse {
    if (state === 'error') {
      return { status, data: DEFAULT_CLIENT_FAIL_RESPONSE };
    }
    if (state === 'fail') {
      return { status, data: getDefaultError(status === 0 ? -1 : status) };
    }
    try {
      let resJson = data;
      if (typeof data === 'string') {
        try {
          resJson = JSON.parse(data);
        } catch (error) {
          resJson = {};
          console.error('解析返回值中的 data 出错: ', data);
        }
      }
      return { status, data: resJson };
    } catch (e) {
      console.error(e);
      return { status, data: DEFAULT_ERROR_RESPONSE } as any;
    }
  }

  // note: 抽象方法： 所有的子类都必须实现的方法(核心方法)
  protected abstract handleSendRequest(url: string): Promise<APIResponse>;
}
